import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Container, 
    TextField, 
    Box, 
    Typography, 
    Button, 
    Alert, 
    Paper, 
    InputAdornment,
    CssBaseline,
    Divider,
    Link
} from "@mui/material";
import { 
    Email as EmailIcon,
    ArrowForward,
    ArrowBack
} from "@mui/icons-material";

import bgImage from '../assets/background_test.png';
import authApi from "../authApi";
// import api from "../api";

const ForgotPassword = ({ navbar }) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setIsLoading(true);

        try {
            const { data } = await authApi.email.post("/forgot-password", { email });
            setSuccess(data.message);
            setEmail(""); // Clear email field after success
        } catch (error) {
            const message = error.response?.data?.message || "Failed to process your request. Please try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    // Quote for the right side (same as your Login component)
    const quote = {
        text: "The brain craves joy. Feed it learning wrapped in play."
    };

    return (
        <Box 
            sx={{
                position: 'relative',
                width: '100%',
                minHeight: 'calc(100vh - 64px)', // Subtract navbar height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), url(${bgImage}) center center / 100% no-repeat`,
                overflow: 'auto',
                mt: 0
            }}
        >
            {/* Render navbar if passed as prop */}
            {/* {navbar} */}

            <CssBaseline />

            <Container 
                maxWidth="md" 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    py: 4
                }}
            >
                <Paper 
                    elevation={10} 
                    sx={{ 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: 3,
                        width: '100%',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                    }}
                >
                    {/* Forgot Password Form Side */}
                    <Box
                        sx={{
                            padding: { xs: 3, sm: 4 },
                            display: 'flex', 
                            flexDirection: 'column',
                            width: { xs: '100%', md: '60%' },
                        }}
                    >
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 700,
                                color: 'primary.main',
                            }}
                        >
                            Forgot Password
                        </Typography>

                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                mb: 4,
                                color: 'text.secondary'
                            }}
                        >
                            Enter your email address and we'll send you a link to reset your password
                        </Typography>

                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mb: 3, 
                                    width: '100%',
                                    borderRadius: 1
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert 
                                severity="success" 
                                sx={{ 
                                    mb: 3, 
                                    width: '100%',
                                    borderRadius: 1
                                }}
                            >
                                {success}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                type="email"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />
                            
                            <Button 
                                type="submit" 
                                variant="contained" 
                                fullWidth 
                                disabled={isLoading}
                                sx={{ 
                                    mt: 2, 
                                    py: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    },
                                    boxShadow: 2
                                }}
                                endIcon={<ArrowForward />}
                            >
                                {isLoading ? "Sending..." : "Send Reset Link"}
                            </Button>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Button
                                    startIcon={<ArrowBack />}
                                    onClick={handleBackToLogin}
                                    sx={{ 
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'primary.main',
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        </form>
                    </Box>

                    {/* Quote Side - same as your Login component */}
                    <Box
                        sx={{
                            width: { xs: '100%', md: '40%' },
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 4,
                            position: 'relative'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                maxWidth: '80%',
                                position: 'relative',
                                py: 4
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: '72px',
                                    fontWeight: 700,
                                    color: 'rgba(255,255,255,0.2)',
                                    position: 'absolute',
                                    top: -10,
                                    left: -20
                                }}
                            >
                                "
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                    mb: 2,
                                    zIndex: 1
                                }}
                            >
                                {quote.text}
                            </Typography>
                            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: '60px', mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: '72px',
                                    fontWeight: 700,
                                    color: 'rgba(255,255,255,0.2)',
                                    position: 'absolute',
                                    bottom: -60,
                                    right: -20
                                }}
                            >
                                "
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPassword;